/**
 * /settings/billing — current plan, trial countdown, upgrade button. Cycle 18.
 *
 * Plan-gate errors elsewhere in the dashboard surface a "you're at
 * your plan limit, upgrade" toast linking back here.
 */
import { useEffect, useState } from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { api } from "../../../lib/api.js";

interface PlanLimits {
  maxFlows: number;
  maxBroadcastsPerMonth: number;
  maxIgAccounts: number;
}

interface BillingStatus {
  plan: "free" | "pro";
  planStatus: "trialing" | "active" | "past_due" | "canceled";
  trialEndsAt: string | null;
  effectivePlan: "free" | "pro";
  limits: PlanLimits;
}

const search = z.object({
  upgrade: z.enum(["ok", "cancel"]).optional(),
});

export const Route = createFileRoute("/_app/settings/billing")({
  validateSearch: search.parse,
  component: BillingPage,
});

function BillingPage() {
  const { upgrade } = useSearch({ from: "/_app/settings/billing" });
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    setError(null);
    try {
      const r = await api.get<BillingStatus>("/billing/status");
      setStatus(r.data);
    } catch {
      setError("Failed to load billing status.");
    }
  }

  async function upgradeClick() {
    setBusy(true);
    try {
      const r = await api.post<{ url: string }>("/billing/checkout-session");
      window.location.href = r.data.url;
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to start checkout. Stripe may not be configured for this environment.");
      setBusy(false);
    }
  }

  async function manageClick() {
    setBusy(true);
    try {
      const r = await api.post<{ url: string }>("/billing/portal-session");
      window.location.href = r.data.url;
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } }).response
        ?.data?.message;
      setError(msg ?? "Failed to open billing portal.");
      setBusy(false);
    }
  }

  if (!status) {
    return (
      <main style={{ maxWidth: 640, margin: "0 auto", padding: "32px 24px" }}>
        <h1 style={{ fontSize: 28 }}>Billing</h1>
        {error ? <div className="brut-error">{error}</div> : <p>Loading…</p>}
      </main>
    );
  }

  const trialDaysLeft = status.trialEndsAt
    ? Math.max(
        0,
        Math.ceil((new Date(status.trialEndsAt).getTime() - Date.now()) / 86400000),
      )
    : null;

  return (
    <main style={{ maxWidth: 640, margin: "0 auto", padding: "32px 24px" }}>
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Billing</h1>

      {upgrade === "ok" ? (
        <div
          className="brut-card"
          style={{ background: "#defbe5", padding: 12, marginBottom: 12 }}
        >
          Upgrade complete. Subscription details may take a moment to sync from
          Stripe.
        </div>
      ) : null}
      {upgrade === "cancel" ? (
        <div
          className="brut-card"
          style={{ background: "#fff7d6", padding: 12, marginBottom: 12 }}
        >
          Upgrade cancelled. You're still on the previous plan.
        </div>
      ) : null}
      {error ? <div className="brut-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      <div className="brut-card" style={{ padding: 16, display: "grid", gap: 12, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>Plan</div>
            <div style={{ fontSize: 24, fontWeight: 800 }}>
              {status.effectivePlan.toUpperCase()}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>Status</div>
            <div style={{ fontSize: 16, fontWeight: 800 }}>{status.planStatus}</div>
          </div>
        </div>
        {status.planStatus === "trialing" && trialDaysLeft != null ? (
          <div
            style={{
              padding: 8,
              background: trialDaysLeft <= 3 ? "#ffd7d7" : "#fff7d6",
              fontSize: 13,
            }}
          >
            {trialDaysLeft > 0
              ? `Trial: ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left.`
              : "Trial ended. Upgrade to keep using Pro features."}
          </div>
        ) : null}
        {status.planStatus === "past_due" ? (
          <div style={{ padding: 8, background: "#ffd7d7", fontSize: 13 }}>
            Payment failed. Update your card via "Manage subscription" before
            the next retry, or your workspace will downgrade to Free.
          </div>
        ) : null}
      </div>

      <div className="brut-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>Limits on this plan</div>
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 4, fontSize: 14 }}>
          <li>Active flows: <strong>{status.limits.maxFlows}</strong></li>
          <li>Broadcasts / month: <strong>{status.limits.maxBroadcastsPerMonth}</strong></li>
          <li>Connected IG accounts: <strong>{status.limits.maxIgAccounts}</strong></li>
        </ul>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        {status.effectivePlan === "free" || status.planStatus === "trialing" ? (
          <button onClick={() => void upgradeClick()} disabled={busy} className="brut-button">
            {busy ? "..." : "Upgrade to Pro"}
          </button>
        ) : null}
        <button
          onClick={() => void manageClick()}
          disabled={busy}
          className="brut-button is-secondary"
        >
          {busy ? "..." : "Manage subscription"}
        </button>
      </div>
    </main>
  );
}
